import type { ServiceFetcher } from '../shared/http.types';
import type {
  ContactByIdResponse,
  ContactRefType,
  ContactsSearchParams,
  ContactsSearchResponse,
  IdListFilter,
} from './contacts.types';

const CONTACTS_PATH = 'contacts';

function joinIdList(filter: IdListFilter | undefined): string | undefined {
  if (filter === undefined) {
    return undefined;
  }
  if (Array.isArray(filter)) {
    return filter.length ? filter.join(',') : undefined;
  }
  return filter.length ? filter : undefined;
}

function buildContactsSearchParams(
  params: ContactsSearchParams,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};

  if (params.search_text) {
    out.search_text = params.search_text;
  }
  const idListFields: Array<[keyof ContactsSearchParams, string]> = [
    ['access_level', 'access_level'],
    ['expertise_level', 'expertise_level'],
    ['languages', 'languages'],
    ['industries', 'industries'],
    ['directions', 'directions'],
    ['competences', 'competences'],
    ['countries', 'countries'],
    ['cities', 'cities'],
  ];
  for (const [field, key] of idListFields) {
    const joined = joinIdList(params[field] as IdListFilter | undefined);
    if (joined !== undefined) {
      out[key] = joined;
    }
  }
  if (params.expand_network !== undefined) {
    out.expand_network = String(params.expand_network);
  }
  if (params.take !== undefined) {
    out.take = params.take;
  }
  if (params.page !== undefined) {
    out.page = params.page;
  }

  return out;
}

/** GET /contacts — union search/browse (registered users, plus salable contacts once enabled server-side). */
export function getContacts(
  fetcher: ServiceFetcher,
  params: ContactsSearchParams = {},
): Promise<ContactsSearchResponse> {
  return fetcher({
    method: 'GET',
    path: CONTACTS_PATH,
    params: buildContactsSearchParams(params),
  }) as Promise<ContactsSearchResponse>;
}

/** GET /contacts/:id — `type` is required; `contacts.id` and `users.id` are independent sequences. */
export function getContactById(
  fetcher: ServiceFetcher,
  id: number,
  type: ContactRefType,
): Promise<ContactByIdResponse> {
  return fetcher({
    method: 'GET',
    path: `${CONTACTS_PATH}/${id}`,
    params: { type },
  }) as Promise<ContactByIdResponse>;
}

/** POST /contacts (multipart) — create a manually-authored contact card. */
export function createContact(fetcher: ServiceFetcher, body: unknown): Promise<unknown> {
  return fetcher({ method: 'POST', path: CONTACTS_PATH, body });
}

/** PATCH /contacts/:id (multipart) — owner-only. */
export function updateContact(
  fetcher: ServiceFetcher,
  id: number,
  body: unknown,
): Promise<unknown> {
  return fetcher({ method: 'PATCH', path: `${CONTACTS_PATH}/${id}`, body });
}

export function deleteContactAvatar(fetcher: ServiceFetcher, id: number): Promise<unknown> {
  return fetcher({ method: 'DELETE', path: `${CONTACTS_PATH}/${id}/delete-avatar` });
}

/** DELETE /contacts/:id — soft delete, owner-only. */
export function deleteContact(fetcher: ServiceFetcher, id: number): Promise<unknown> {
  return fetcher({ method: 'DELETE', path: `${CONTACTS_PATH}/${id}` });
}

export function restoreContact(fetcher: ServiceFetcher, id: number): Promise<unknown> {
  return fetcher({ method: 'PATCH', path: `${CONTACTS_PATH}/${id}/restore` });
}

/** POST /contacts/:id/copy — "buy" a salable contact as an independent owned copy. */
export function copyContact(fetcher: ServiceFetcher, id: number): Promise<unknown> {
  return fetcher({ method: 'POST', path: `${CONTACTS_PATH}/${id}/copy` });
}
